import { useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X, ChevronRight, ArrowUpRight, Share, Megaphone } from "lucide-react";
import type { Announcement } from "../services/AnnouncementService";
import { AnnouncementBody } from "../utils/textUtils";
import { cleanAnnouncementBody, formatUsd, parseAnnouncementDeal, type AnnouncementDeal } from "../utils/alertFeed";
import { calmCase, formatRelativeTime, headlineOf, localized, mentionsPrice, percentLabel, withoutRepeatedTitle, type Lang } from "../utils/announcementText";

// Shared look for the announcement popup and the bell inbox.

// Announcement images can be hosted by posting tools that delete them later;
// a dead image shows the fallback instead of an empty box.
function ListThumb({ src, className, fallback }: { src: string; className: string; fallback: ReactNode }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <>{fallback}</>;
  return <img src={src} alt="" loading="lazy" className={className} onError={() => setFailed(true)} />;
}

export function RowThumb({ src, icon }: { src?: string; icon: ReactNode }) {
  const fallback = <span className="text-[#8a849b] dark:text-dark-400">{icon}</span>;
  return (
    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#f5f4f7] ring-1 ring-black/[0.04] dark:bg-dark-700 dark:ring-white/5">
      {src ? <ListThumb src={src} className="h-full w-full object-cover" fallback={fallback} /> : fallback}
    </div>
  );
}

// Photo on top of the article; tap to see it full screen.
function HeroImage({ imageUrl, children }: { imageUrl: string; children?: ReactNode }) {
  const [enlarged, setEnlarged] = useState(false);
  const [failed, setFailed] = useState(false);
  if (failed) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setEnlarged(true)}
        aria-label="View image"
        className="relative mb-4 block aspect-[16/10] w-full overflow-hidden rounded-2xl bg-[#f5f4f7] dark:bg-white"
      >
        <img src={imageUrl} alt="" onError={() => setFailed(true)} className="h-full w-full object-contain p-3 mix-blend-multiply" />
        {children}
      </button>
      {enlarged && createPortal(
        <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/85 p-4" onClick={() => setEnlarged(false)}>
          <button
            type="button"
            onClick={() => setEnlarged(false)}
            className="absolute top-4 right-4 rounded-full p-2 text-white/90 hover:bg-white/10 hover:text-white"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <img src={imageUrl} alt="" className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
        </div>,
        document.body
      )}
    </>
  );
}

function ShareButton({ link, title }: { link: string; title: string }) {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title, url: link });
      } else {
        await navigator.clipboard.writeText(link);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }
    } catch {
      // share sheet dismissed
    }
  };
  return (
    <button
      type="button"
      onClick={share}
      aria-label={copied ? "Link copied" : "Share"}
      className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#f3f1f7] text-[#3d3650] transition hover:bg-[#e9e5f1] dark:bg-dark-700 dark:text-dark-100 dark:hover:bg-dark-600"
    >
      <Share className="h-[17px] w-[17px]" />
    </button>
  );
}

function DealPrice({ deal, lang }: { deal: AnnouncementDeal; lang: Lang }) {
  const saved = deal.price != null && deal.wasPrice != null && deal.wasPrice > deal.price ? deal.wasPrice - deal.price : null;
  return (
    <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-1 tabular-nums">
      <span className="text-[26px] leading-none font-bold tracking-[-0.02em] text-[#17122b] dark:text-white">{formatUsd(deal.price!)}</span>
      {saved != null && (
        <>
          <span className="text-sm text-[#8a849b] line-through dark:text-dark-400">{formatUsd(deal.wasPrice!)}</span>
          <span className="text-sm font-semibold text-green-700 dark:text-green-400">
            {lang === "es" ? `Ahorras ${formatUsd(saved)}` : `Save ${formatUsd(saved)}`}
          </span>
        </>
      )}
    </div>
  );
}

// The open announcement: a deal shows its price and a shop button,
// anything else (feature news, notices, social posts) reads like a short article.
export function AnnouncementHero({ announcement, lang, isNew = false }: { announcement: Announcement; lang: Lang; isNew?: boolean }) {
  const { title, body } = localized(announcement, lang);
  const deal = parseAnnouncementDeal(announcement);
  const priced = !deal && mentionsPrice(announcement);
  const link = announcement.link;
  const headline = deal?.name ? calmCase(deal.name) : headlineOf(title);
  const text = cleanAnnouncementBody(withoutRepeatedTitle(body, title), link);
  const ctaLabel = deal?.retailer
    ? (lang === "es" ? `Ver en ${deal.retailer}` : `View at ${deal.retailer}`)
    : deal || priced
      ? (lang === "es" ? "Ver oferta" : "View deal")
      : (lang === "es" ? "Más información" : "Learn more");

  return (
    <article className="px-5 pt-5 pb-6">
      {announcement.imageUrl && (
        <HeroImage imageUrl={announcement.imageUrl}>
          {deal?.percentOff ? (
            <span className="absolute top-3 left-3 inline-flex h-6 items-center rounded-full bg-green-600 px-2.5 text-xs font-semibold text-white tabular-nums">
              −{deal.percentOff}%
            </span>
          ) : null}
        </HeroImage>
      )}

      <p className="m-0 flex flex-wrap items-center gap-1.5 text-[13px] text-[#8a849b] dark:text-dark-400">
        {isNew && (
          <>
            <span className="font-semibold text-[#6d28d9] dark:text-purple-300">{lang === "es" ? "Nuevo" : "New"}</span>
            <span aria-hidden="true">·</span>
          </>
        )}
        {deal?.retailer && (
          <>
            <span className="font-medium text-[#3d3650] dark:text-dark-200">{deal.retailer}</span>
            <span aria-hidden="true">·</span>
          </>
        )}
        <span>{formatRelativeTime(announcement.createdAt, lang)}</span>
      </p>

      <h2 className={`m-0 mt-1.5 text-[#17122b] dark:text-white ${deal ? "line-clamp-2 text-[17px] leading-[23px] font-semibold" : "text-xl leading-[26px] font-bold tracking-[-0.01em]"}`}>
        {headline}
      </h2>

      {deal?.price != null && <DealPrice deal={deal} lang={lang} />}

      {!deal && text && (
        <AnnouncementBody body={text} className="mt-2 space-y-3 text-[15px] leading-6 text-[#4a4459] dark:text-dark-300" />
      )}

      {link && (
        <div className="mt-5 flex gap-2">
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-11 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#6d28d9] px-4 text-[15px] font-semibold text-white transition hover:bg-[#5b21b6] active:scale-[0.99]"
          >
            <span className="truncate">{ctaLabel}</span>
            <ArrowUpRight className="h-4 w-4 flex-shrink-0 opacity-80" />
          </a>
          <ShareButton link={link} title={headline} />
        </div>
      )}
    </article>
  );
}

// One list row: square thumbnail, one-line title, grey meta line, chevron (or a trailing action beside it).
export function FeedRow({ thumb, title, meta, onClick, trailing }: { thumb: ReactNode; title: string; meta: ReactNode; onClick: () => void; trailing?: ReactNode }) {
  return (
    <div className="group relative">
      <button type="button" onClick={onClick} className={`flex w-full items-center gap-3 py-2.5 pl-5 text-left transition hover:bg-[#f7f6fa] dark:hover:bg-dark-700/60 ${trailing ? "pr-12" : "pr-5"}`}>
        {thumb}
        <div className="min-w-0 flex-1">
          <p className="m-0 truncate text-[15px] font-medium text-[#17122b] dark:text-white" title={title}>{title}</p>
          <p className="m-0 mt-0.5 truncate text-[13px] text-[#8a849b] tabular-nums dark:text-dark-400">{meta}</p>
        </div>
        {!trailing && <ChevronRight className="h-4 w-4 flex-shrink-0 text-[#c4bfd0] transition group-hover:text-[#8a849b] dark:text-dark-500" />}
      </button>
      {trailing && <div className="absolute top-1/2 right-3 -translate-y-1/2">{trailing}</div>}
    </div>
  );
}

export function AnnouncementRow({ announcement, lang, onClick }: { announcement: Announcement; lang: Lang; onClick: () => void }) {
  const { title, body } = localized(announcement, lang);
  const deal = parseAnnouncementDeal(announcement);
  const time = formatRelativeTime(announcement.createdAt, lang);
  const meta = deal ? (
    <>
      {deal.percentOff ? <span className="font-medium text-green-700 dark:text-green-400">{percentLabel(deal.percentOff, lang)}</span> : null}
      {deal.price != null && <>{deal.percentOff ? " · " : ""}{formatUsd(deal.price)}</>}
      {" · "}{time}
    </>
  ) : time;

  return (
    <FeedRow
      onClick={onClick}
      thumb={<RowThumb src={announcement.imageUrl} icon={<Megaphone className="h-5 w-5" />} />}
      title={deal?.name ? calmCase(deal.name) : headlineOf(title) || headlineOf(body)}
      meta={meta}
    />
  );
}

export function EmptyState({ icon, title, hint }: { icon: ReactNode; title: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center justify-center px-8 py-16 text-center text-[#8a849b] dark:text-dark-400">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#f5f4f7] dark:bg-dark-700">{icon}</div>
      <p className="m-0 text-[15px] font-medium text-[#3d3650] dark:text-dark-200">{title}</p>
      {hint && <p className="m-0 mt-1 text-[13px]">{hint}</p>}
    </div>
  );
}

export function TabButton({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count: number }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`relative -mb-px flex h-11 items-center gap-1.5 border-b-2 text-[15px] transition ${
        active
          ? "border-[#6d28d9] font-semibold text-[#17122b] dark:border-purple-400 dark:text-white"
          : "border-transparent font-medium text-[#8a849b] hover:text-[#3d3650] dark:text-dark-400 dark:hover:text-dark-200"
      }`}
    >
      {label}
      {count > 0 && (
        <span className={`text-[13px] tabular-nums ${active ? "font-semibold text-[#6d28d9] dark:text-purple-300" : "text-[#a7a2b3] dark:text-dark-500"}`}>{count}</span>
      )}
    </button>
  );
}

